import React, { useEffect, useMemo, useState } from "react";
import { PickerView } from "antd-mobile";
import dayjs from "dayjs";
import BottomSheet from "../BottomSheet";
import styles from "./index.module.scss";

interface DateTimeBottomPickerProps {
  visible: boolean;
  title?: string;
  value?: string; // 当前值 "YYYY-MM-DD HH:mm"
  max?: Date; // 可选最大时间（不晚于），默认当前时间
  rangeDays?: number; // 日期列往前的天数（默认 365）
  onClose: () => void;
  onConfirm: (value: string) => void; // 回传 "YYYY-MM-DD HH:mm"
  zIndex?: number;
  /**
   * 允许「未知」时/分。开启后时、分列首各加「未知」选项：
   * - 选「时=未知」→ 分强制为未知，回传 "YYYY-MM-DD --:--"
   * - 选「时=具体值 + 分=未知」→ 回传 "YYYY-MM-DD HH:--"
   * 关闭时（默认）行为与原来完全一致。
   */
  allowUnknown?: boolean;
}

const WEEK = ["日", "一", "二", "三", "四", "五", "六"];
const pad2 = (n: number) => String(n).padStart(2, "0");
const rangeCol = (to: number) =>
  Array.from({ length: to + 1 }, (_, i) => ({
    label: pad2(i),
    value: pad2(i),
  }));

// 未知标记（存于 "HH:mm" 槽位；提交时由上层补 "00"）
const UNKNOWN = "--";
const UNKNOWN_OPT = { label: "未知", value: UNKNOWN };

/**
 * 底部 3 列日期时间选择器（日期「M月D日 周X」/ 时 / 分），不晚于 max（默认当前时间）。
 * 用 PickerView 三独立列 + onChange 动态收敛（选到今天/当前时时限制时、分），避免级联全量树。
 */
export default function DateTimeBottomPicker(props: DateTimeBottomPickerProps) {
  const {
    visible,
    title = "选择时间",
    value = "",
    max,
    rangeDays = 365,
    onClose,
    onConfirm,
    zIndex = 2000,
    allowUnknown = false,
  } = props;

  // 打开时刷新"当前时间"基准（避免长时间停留导致 now 漂移）
  const [nowTick, setNowTick] = useState(0);
  const maxDay = useMemo(
    () => dayjs(max || new Date()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [max, nowTick]
  );
  const todayStr = maxDay.format("YYYY-MM-DD");
  const maxHour = maxDay.hour();
  const maxMinute = maxDay.minute();

  // 日期列（rangeDays 天前 → 今天，升序）
  const dateCol = useMemo(
    () =>
      Array.from({ length: rangeDays + 1 }, (_, i) => {
        const d = maxDay.subtract(rangeDays - i, "day");
        return {
          label: `${d.format("M月D日")} 周${WEEK[d.day()]}`,
          value: d.format("YYYY-MM-DD"),
        };
      }),
    [maxDay, rangeDays]
  );

  const [val, setVal] = useState<string[]>([]);

  // 打开时刷新 now，并用 value 初始化（否则默认 max）；回显值超出范围则钳到 [起点, max]
  useEffect(() => {
    if (!visible) return;
    setNowTick((t) => t + 1);
    const now = dayjs(max || new Date());
    const minDay = now.subtract(rangeDays, "day");

    // 解析回显值（allowUnknown 时时/分可能为 "--"）
    const [dPart = "", tPart = ""] = String(value || "").split(" ");
    const [hPart = "", mPart = ""] = tPart.split(":");
    const hUnknown = allowUnknown && hPart === UNKNOWN;
    const mUnknown = allowUnknown && mPart === UNKNOWN;

    // 日期：无有效值默认今天，钳到 [minDay, now]（按天）
    let baseDay = dPart && dayjs(dPart).isValid() ? dayjs(dPart) : now;
    if (baseDay.isAfter(now, "day")) baseDay = now;
    if (baseDay.isBefore(minDay, "day")) baseDay = minDay;
    const dateStr = baseDay.format("YYYY-MM-DD");

    // 时：未知保留；否则回显原值，无值默认 00
    let hh = hUnknown
      ? UNKNOWN
      : hPart !== ""
      ? pad2(Number(hPart))
      : "00";
    // 分：小时未知则强制未知；否则未知保留 / 回显 / 无值默认 00
    let mm =
      hh === UNKNOWN
        ? UNKNOWN
        : mUnknown
        ? UNKNOWN
        : mPart !== ""
        ? pad2(Number(mPart))
        : "00";

    // 已知时刻若超过当天上限则钳制（防越界）
    const todayNow = now.format("YYYY-MM-DD");
    if (dateStr === todayNow && hh !== UNKNOWN && Number(hh) > now.hour()) {
      hh = pad2(now.hour());
    }
    if (
      dateStr === todayNow &&
      hh !== UNKNOWN &&
      Number(hh) === now.hour() &&
      mm !== UNKNOWN &&
      Number(mm) > now.minute()
    ) {
      mm = pad2(now.minute());
    }

    setVal([dateStr, hh, mm]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const isToday = val[0] === todayStr;
  const isHourUnknown = allowUnknown && val[1] === UNKNOWN;
  const isCurHour = isToday && Number(val[1]) === maxHour;
  const hourCol = useMemo(() => {
    const base = rangeCol(isToday ? maxHour : 23);
    return allowUnknown ? [UNKNOWN_OPT, ...base] : base;
  }, [isToday, maxHour, allowUnknown]);
  const minCol = useMemo(() => {
    // 小时未知 → 分钟只能未知
    if (isHourUnknown) return [UNKNOWN_OPT];
    const base = rangeCol(isCurHour ? maxMinute : 59);
    return allowUnknown ? [UNKNOWN_OPT, ...base] : base;
  }, [isHourUnknown, isCurHour, maxMinute, allowUnknown]);

  // 滚动变化：选到今天/当前时时，收敛时、分，超出则钳制
  const handleChange = (v: (string | number | null)[]) => {
    let [d, h, m] = v.map((x) => String(x ?? ""));
    // 小时未知 → 分钟强制未知
    if (allowUnknown && h === UNKNOWN) {
      setVal([d, UNKNOWN, UNKNOWN]);
      return;
    }
    const today = d === todayStr;
    const mh = today ? maxHour : 23;
    if (Number(h) > mh) h = pad2(mh);
    // 分钟未知：保留（此时小时已是具体值）
    if (allowUnknown && m === UNKNOWN) {
      setVal([d, h, UNKNOWN]);
      return;
    }
    const curH = today && Number(h) === maxHour;
    const mm = curH ? maxMinute : 59;
    if (Number(m) > mm) m = pad2(mm);
    setVal([d, h, m]);
  };

  const handleConfirm = () => {
    if (val.length === 3) onConfirm(`${val[0]} ${val[1]}:${val[2]}`);
    onClose();
  };

  return (
    <BottomSheet
      visible={visible}
      title={title}
      onClose={onClose}
      onConfirm={handleConfirm}
      zIndex={zIndex}
    >
      <div className={styles.pickerRow}>
        <PickerView
          columns={[dateCol, hourCol, minCol]}
          value={val}
          onChange={handleChange}
        />
        {/* 时/分之间的「:」：分钟为未知时隐藏，避免出现「14 : 未知」的怪异观感 */}
        {val[2] !== UNKNOWN && <span className={styles.colon}>:</span>}
      </div>
    </BottomSheet>
  );
}
